export type OtsGoBotState = "walking" | "idle" | "hello" | "cheer";

export type OtsGoBotProps = {
  /**
   * `walking` strides in place for work in progress; `idle` stands still; `hello` plays once when GoBot first
   * appears (brackets close in, the hat drops on) and then holds the idle pose; `cheer` plays once when work
   * finishes (a hop, a tip of the hat, the spark turning) and then holds the idle pose.
   */
  state?: OtsGoBotState;
  /** Rendered width and height in pixels. */
  size?: number;
  /** Spoken name; leave out when nearby text already says what is happening. */
  title?: string;
  className?: string;
};

// Motion from docs/brand/gobot/build-gobot.mjs (Crew), scoped by state class (`.ots-gobot-walking`, `.ots-gobot-hello`).
const goBotCss = [
  ".ots-gobot g{transform-box:view-box;transform-origin:0 0}",
  ".ots-gobot .gb-walker{transform:scale(.76)}",
  ".ots-gobot .gb-eyes{transform:translateX(.7px)}",
  ".ots-gobot .gb-dash{opacity:0}",
  ".ots-gobot-walking .gb-dash{opacity:.5}",
  ".ots-gobot-walking .gb-hat{animation:ots-gobot-bob .45s cubic-bezier(.45,0,.55,1) .35s infinite both}",
  ".ots-gobot-walking .gb-walker{animation:ots-gobot-shrink .35s cubic-bezier(.2,.8,.2,1) both}",
  ".ots-gobot-walking .gb-legs{animation:ots-gobot-legs-in .35s cubic-bezier(.2,.8,.2,1) both}",
  ".ots-gobot-walking .gb-stride{animation:ots-gobot-stride .9s cubic-bezier(.45,0,.55,1) .35s infinite both}",
  ".ots-gobot-walking .gb-leg-r{animation:ots-gobot-step-r .9s cubic-bezier(.45,0,.55,1) .35s infinite both}",
  ".ots-gobot-walking .gb-leg-l{animation:ots-gobot-step-l .9s cubic-bezier(.45,0,.55,1) .35s infinite both}",
  ".ots-gobot .gb-eye{animation:ots-gobot-blink 3.6s cubic-bezier(.45,0,.55,1) infinite both}",
  ".ots-gobot-walking .gb-dash-0{animation:ots-gobot-ground .9s linear .35s infinite both}",
  ".ots-gobot-walking .gb-dash-1{animation:ots-gobot-ground .9s linear .05s infinite both}",
  ".ots-gobot-walking .gb-dash-2{animation:ots-gobot-ground .9s linear -.25s infinite both}",
  "@keyframes ots-gobot-bob{0%,100%{transform:translateY(-.4px)}50%{transform:translateY(.35px)}}",
  "@keyframes ots-gobot-shrink{0%{transform:none}100%{transform:scale(.76)}}",
  "@keyframes ots-gobot-legs-in{0%{transform:translateY(-6.5px);opacity:0}60%{opacity:1}100%{transform:none;opacity:1}}",
  "@keyframes ots-gobot-stride{0%,50%,100%{transform:none}25%{transform:translateY(-1.3px) rotate(-4deg)}75%{transform:translateY(-1.3px) rotate(4deg)}}",
  "@keyframes ots-gobot-step-r{0%,50%,100%{transform:none}25%{transform:translateY(-2.6px)}}",
  "@keyframes ots-gobot-step-l{0%,50%,100%{transform:none}75%{transform:translateY(-2.6px)}}",
  "@keyframes ots-gobot-blink{0%,70%,76%,100%{transform:none}73%{transform:scaleY(.1)}}",
  "@keyframes ots-gobot-ground{0%{transform:translateX(5px);opacity:0}20%,80%{opacity:.5}100%{transform:translateX(-5px);opacity:0}}",
  "@keyframes ots-gobot-pulse{50%{opacity:.45}}",
  // Hello (Crew), played once: each bracket flies in from its corner, the face rises, the hat drops on with a squash,
  // then the spark turns and the eyes blink.
  ".ots-gobot-hello .gb-br-tl{animation:ots-gobot-hello-tl .75s cubic-bezier(.2,.8,.2,1) 0s both}",
  ".ots-gobot-hello .gb-br-tr{animation:ots-gobot-hello-tr .75s cubic-bezier(.2,.8,.2,1) .06s both}",
  ".ots-gobot-hello .gb-br-br{animation:ots-gobot-hello-br .75s cubic-bezier(.2,.8,.2,1) .12s both}",
  ".ots-gobot-hello .gb-br-bl{animation:ots-gobot-hello-bl .75s cubic-bezier(.2,.8,.2,1) .18s both}",
  ".ots-gobot-hello .gb-face{animation:ots-gobot-hello-face .7s cubic-bezier(.2,.8,.2,1) .1s both}",
  ".ots-gobot-hello .gb-hat{animation:ots-gobot-hello-hat .8s cubic-bezier(.2,.8,.2,1) .42s both}",
  ".ots-gobot-hello .gb-emblem{animation:ots-gobot-hello-emblem 1.7s cubic-bezier(.45,0,.55,1) both}",
  ".ots-gobot-hello .gb-eye{animation:ots-gobot-hello-blink 1.7s cubic-bezier(.45,0,.55,1) both}",
  "@keyframes ots-gobot-hello-tl{0%{transform:translate(-4.5px,-4.5px);opacity:0}55%{transform:translate(.8px,.8px);opacity:1}100%{transform:none;opacity:1}}",
  "@keyframes ots-gobot-hello-tr{0%{transform:translate(4.5px,-4.5px);opacity:0}55%{transform:translate(-.8px,.8px);opacity:1}100%{transform:none;opacity:1}}",
  "@keyframes ots-gobot-hello-br{0%{transform:translate(4.5px,4.5px);opacity:0}55%{transform:translate(-.8px,-.8px);opacity:1}100%{transform:none;opacity:1}}",
  "@keyframes ots-gobot-hello-bl{0%{transform:translate(-4.5px,4.5px);opacity:0}55%{transform:translate(.8px,-.8px);opacity:1}100%{transform:none;opacity:1}}",
  "@keyframes ots-gobot-hello-face{0%{transform:translateY(7px) scale(.85);opacity:0}55%{transform:translateY(-.9px) scale(1.02);opacity:1}100%{transform:none;opacity:1}}",
  "@keyframes ots-gobot-hello-hat{0%{transform:translateY(-12px);opacity:0;animation-timing-function:cubic-bezier(.55,0,1,.45)}25%{opacity:1}45%{transform:scale(1.06,.9)}68%{transform:translateY(-1.6px) scale(.98,1.03)}86%{transform:scale(1.01,.99)}100%{transform:none;opacity:1}}",
  "@keyframes ots-gobot-hello-emblem{0%,74%{transform:none}84%{transform:scale(1.5) rotate(45deg)}100%{transform:rotate(90deg)}}",
  "@keyframes ots-gobot-hello-blink{0%,80%,90%,100%{transform:none}85%{transform:scaleY(.1)}}",
  // Reduced motion: the bot stands still and only the brackets breathe.
  "@media (prefers-reduced-motion:reduce){.ots-gobot g{animation:none!important}.ots-gobot-walking .gb-br{animation:ots-gobot-pulse 2.4s ease-in-out infinite!important}.ots-gobot .gb-dash{opacity:0}}",
  // Cheer, played once when work is done: a hop with a landing squash, the hat tipped and caught, the spark turned.
  ".ots-gobot-cheer .gb-stride{animation:ots-gobot-cheer-hop .9s cubic-bezier(.3,.7,.4,1) both}",
  ".ots-gobot-cheer .gb-hat{animation:ots-gobot-cheer-hat .9s cubic-bezier(.3,.7,.4,1) .05s both}",
  ".ots-gobot-cheer .gb-emblem{animation:ots-gobot-cheer-emblem 1.1s cubic-bezier(.45,0,.55,1) .1s both}",
  ".ots-gobot-cheer .gb-eye{animation:ots-gobot-hello-blink 1.2s cubic-bezier(.45,0,.55,1) both}",
  "@keyframes ots-gobot-cheer-hop{0%{transform:none}30%{transform:translateY(-3.2px)}55%{transform:none}66%{transform:scale(1.05,.93)}82%,100%{transform:none}}",
  "@keyframes ots-gobot-cheer-hat{0%{transform:none}32%{transform:translateY(-2.4px) rotate(-12deg)}60%{transform:rotate(3deg)}80%,100%{transform:none}}",
  "@keyframes ots-gobot-cheer-emblem{0%{transform:none}100%{transform:rotate(180deg)}}",
].join("");

const BRACKET = "#E1251B";
// The body follows the text color (navy on light, near-white on dark); details take the surface behind it.
const BODY = "currentColor";
const DETAIL = "var(--ots-gobot-detail, var(--ots-surface, #ffffff))";

/**
 * GoBot (Crew): the OTS assistant mascot in the red corner brackets. Foundational, so loaders, empty states,
 * and AI features can share one drawing; it inherits its color from the text around it.
 */
export const OtsGoBot = ({ state = "idle", size = 36, title, className }: OtsGoBotProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 36 36"
    fill="none"
    role={title ? "img" : undefined}
    aria-label={title}
    aria-hidden={title ? undefined : true}
    data-state={state}
    className={["ots-gobot", `ots-gobot-${state}`, "shrink-0 text-[var(--ots-secondary)]", className].filter(Boolean).join(" ")}>
    <style>{goBotCss}</style>
    <g className="gb-br gb-br-tl"><path fill={BRACKET} d="M0 0H10.27V5.08H5.08V10.16H0Z"/></g>
    <g className="gb-br gb-br-tr"><path fill={BRACKET} d="M25.73 0H35.89V10.27H30.81V5.08H25.73Z"/></g>
    <g className="gb-br gb-br-br"><path fill={BRACKET} d="M35.89 25.74V35.9H25.62V30.82H30.81V25.74Z"/></g>
    <g className="gb-br gb-br-bl"><path fill={BRACKET} d="M0.16 25.63H5.24V30.82H10.32V35.9H0.16Z"/></g>
    <g transform="translate(17.95 15.1)">
      <g className="gb-walker">
        <g transform="translate(-17.95 -15.1)">
          <g className="gb-legs">
            <g className="gb-leg-l">
              <rect x="13.15" y="25.5" width="2.8" height="7.6" rx="1.4" fill={BODY}/>
              <rect x="11.85" y="32.5" width="4.6" height="2.2" rx="1.1" fill={BODY}/>
            </g>
            <g className="gb-leg-r">
              <rect x="19.95" y="25.5" width="2.8" height="7.6" rx="1.4" fill={BODY}/>
              <rect x="19.45" y="32.5" width="4.6" height="2.2" rx="1.1" fill={BODY}/>
            </g>
          </g>
          <g transform="translate(17.95 34.7)">
            <g className="gb-stride">
              <g transform="translate(-17.95 -34.7)">
                <g transform="translate(17.95 22.9)">
                  <g className="gb-face">
                    <rect x="-8" y="-5.8" width="16" height="11.6" rx="3.6" fill={BODY}/>
                    <g className="gb-eyes">
                      <g transform="translate(-4 -1.2)"><g className="gb-eye"><rect x="-2" y="-2" width="4" height="4" rx="2" fill={DETAIL}/></g></g>
                      <g transform="translate(4 -1.2)"><g className="gb-eye"><rect x="-2" y="-2" width="4" height="4" rx="2" fill={DETAIL}/></g></g>
                    </g>
                  </g>
                </g>
                <g transform="translate(17.95 15.1)">
                  <g className="gb-hat">
                    <path fill={BODY} d="M-7.1 0.2V-2.99C-7.1 -6.35 -3.91 -8.2 0 -8.2C3.91 -8.2 7.1 -6.35 7.1 -2.99V0.2Z"/>
                    <rect x="-2.2" y="-9.1" width="4.4" height="3.9" rx="1.4" fill={BODY}/>
                    <path d="M-10.35 0.75Q0 -2.25 10.35 0.75" stroke={BODY} strokeWidth="2.7" strokeLinecap="round"/>
                    <path d="M-2.2 -7.6V-2.6" stroke={DETAIL} strokeWidth="0.6" strokeLinecap="round"/>
                    <path d="M2.2 -7.6V-2.6" stroke={DETAIL} strokeWidth="0.6" strokeLinecap="round"/>
                    <g transform="translate(0 -4.4)">
                      <g className="gb-emblem">
                        <path fill={DETAIL} d="M0 -1.75Q0.28 -0.28 1.75 0Q0.28 0.28 0 1.75Q-0.28 0.28 -1.75 0Q-0.28 -0.28 0 -1.75Z"/>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
      </g>
    </g>
    <g>
      {[0, 1, 2].map((index) => (
        <g key={index} className={`gb-dash gb-dash-${index}`}>
          <rect x="16.75" y="31.3" width="2.4" height="0.9" rx="0.45" fill={BODY}/>
        </g>
      ))}
    </g>
  </svg>
);
